import { Platform } from "expo-modules-core";
import { useNavigation } from "expo-router";
import { type ReactNode, useCallback, useEffect, useId, useMemo, useState } from "react";
import { Modal, type ModalProps, StyleSheet, View, type ViewProps, type ViewStyle } from "react-native";
import type { StyleProp } from "react-native/Libraries/StyleSheet/StyleSheet";
import type { NativeSyntheticEvent } from "react-native/Libraries/Types/CoreEventTypes";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import Animated, { useSharedValue, withTiming } from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Button } from "~/components/Button";
import { FeedbackPortal } from "~/components/feedback/FeedbackPortal";
import { ConfirmationModal } from "~/components/layout/modal/ConfirmationModal";
import { ModalBackdrop } from "~/components/layout/modal/ModalBackdrop";
import { Title } from "~/components/layout/text";
import { PaperTheme } from "~/components/PaperTheme";
import { Nothing } from "~/components/permission/Nothing";
import { Only } from "~/components/permission/Only";
import { Flex, Gap, Items, Padding, Rounded, Width } from "~/constants/layout";
import { Space } from "~/constants/sizing";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useModal } from "~/providers/ModalProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import { type LayoutEventHandler, setHeightOnLayout, setWidthOnLayout, titleWidth } from "~/util/layout";

type ElementRenderer = () => ReactNode;

export type FullPageModalProps = {
    readonly name?: string;
    title?: ReactNode;
    cancelable?: boolean;
    dirty?: boolean;
    withDone?: boolean;
    headerLeft?: ElementRenderer;
    headerTitle?: ElementRenderer;
    headerRight?: ElementRenderer;
    hideHeader?: boolean;
    children?: ReactNode;
    footer?: ElementRenderer;
    footerProps?: Omit<ViewProps, "children">;
    bodyStyle?: StyleProp<ViewStyle>;
    onBodyLayout?: LayoutEventHandler;
    gap?: number;
    readonly asRoute?: boolean;
    readonly scrollable?: boolean;
} & ModalProps;

export const FullPageModal = ({
    title,
    cancelable,
    dirty,
    withDone,
    headerLeft,
    headerTitle,
    headerRight,
    hideHeader,
    children,
    footer,
    footerProps,
    bodyStyle,
    asRoute,
    name,
    onRequestClose,
    onBodyLayout,
    gap = Space.md,
    scrollable = true,
    ...modalProps
}: FullPageModalProps) => {
    const { colours, background, paperTheme } = useTheme();
    const { saving, loading, error, setError } = useFeedback();
    const navigation = useNavigation();
    const { topFullPageModal, modalStack, fullPageModalStack, stackDepth, openFullPage, closeFullPage } = useModal();
    const modalId = useId();
    // biome-ignore lint/correctness/useExhaustiveDependencies(name): name
    const id = useMemo(() => `${name ?? ""}${modalId}`, [modalId]);

    const level = Math.max(0, modalStack.indexOf(modalId));
    const zIndex = (level + 1) * 1000;
    const insets = useSafeAreaInsets();
    const [confirm, setConfirm] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies(openFullPage): action
    // biome-ignore lint/correctness/useExhaustiveDependencies(closeFullPage): action
    useEffect(() => {
        openFullPage(id);
        return () => {
            closeFullPage(id);
        };
    }, [id]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(asRoute): readonly
    useEffect(() => {
        asRoute &&
            navigation.setOptions({
                presentation: "transparentModal",
                animation: "fade",
                headerLeft: () => <Nothing />,
            });
    }, [navigation]);

    const pageLevel = useMemo(() => fullPageModalStack.indexOf(modalId), [fullPageModalStack, modalId]);
    const isTopPage = useMemo(() => topFullPageModal === modalId, [topFullPageModal, modalId]);
    const topInset = useSharedValue(112);
    const xInset = useSharedValue(0);

    useEffect(() => {
        if (isTopPage || pageLevel === -1) {
            topInset.value = 112;
            xInset.value = 0;
        } else {
            setTimeout(() => {
                topInset.value = withTiming(90 + pageLevel * (22 / stackDepth));
                xInset.value = withTiming(6 + pageLevel * (6 / stackDepth));
            }, 150);
        }
    }, [isTopPage, pageLevel, topInset, xInset, stackDepth]);

    const [width, setWidth] = useState(300);
    const [minHeight, setMinHeight] = useState(500);
    const [leftWidth, setLeftWidth] = useState(0);
    const [rightWidth, setRightWidth] = useState(0);
    const handleOnBodyLayout = setHeightOnLayout(setMinHeight, onBodyLayout);
    const onHeaderLayout = setWidthOnLayout(setWidth);
    const onLeftLayout = setWidthOnLayout(setLeftWidth);
    const onRightLayout = setWidthOnLayout(setRightWidth);
    const { center: center_width, sides: side_width } = useMemo(
        () => titleWidth(width, 4, leftWidth, rightWidth),
        [width, leftWidth, rightWidth],
    );

    const handle_close = useCallback(
        (event: NativeSyntheticEvent<unknown>) => {
            if (dirty && !confirm) {
                setConfirm(true);
            } else {
                onRequestClose?.(event);
            }
        },
        [dirty, confirm, onRequestClose],
    );

    const render_left = useMemo<ElementRenderer | null>(
        () =>
            headerLeft ??
            (cancelable
                ? () => <Button variant="destructive-quiet" size="sm" title="Cancel" onPress={handle_close} />
                : null),
        [headerLeft, cancelable, handle_close],
    );
    const render_title = useMemo<ElementRenderer | null>(
        () =>
            headerTitle ??
            (title
                ? () => (
                      <Title align="center" size="base" style={Flex.grow}>
                          {title}
                      </Title>
                  )
                : null),
        [headerTitle, title],
    );

    const render_right = useMemo<ElementRenderer | null>(
        () =>
            headerRight ??
            (withDone ? () => <Button variant="secondary" title="Done" size="sm" onPress={handle_close} /> : null),
        [headerRight, withDone, handle_close],
    );
    const show_header = useMemo(
        () => !hideHeader && Boolean(render_left ?? render_title ?? render_right),
        [hideHeader, render_left, render_title, render_right],
    );

    const body_style = [{ gap, minHeight }, bodyStyle];

    return (
        <Modal
            {...modalProps}
            animationType="fade"
            style={{ zIndex }}
            statusBarTranslucent={true}
            presentationStyle="overFullScreen"
            transparent={Platform.select({
                web: true,
                default: false,
            })}
            backdropColor={colours.backdrop}
            onRequestClose={handle_close}
        >
            <ModalBackdrop />
            <PaperTheme theme={paperTheme}>
                <Only
                    when={isTopPage}
                    show={() => (
                        <FeedbackPortal saving={filled(saving)} loading={loading} error={error} setError={setError} />
                    )}
                />
                <Animated.View
                    style={[
                        background.secondary,
                        Rounded.top_md,
                        Padding.horizontal_md,
                        Padding.vertical_sm,
                        Gap.sm,
                        Items.stretch,
                        {
                            flex: 1,
                            position: "absolute",
                            bottom: 0,
                            top: 112,
                            left: 0,
                            right: 0,
                            paddingBottom: insets.bottom + Space.md,
                        },
                        {
                            top: topInset,
                            left: xInset,
                            right: xInset,
                        },
                    ]}
                >
                    {show_header && (
                        <View
                            style={[Width.full, Flex.row, Gap.xxs, Items.center, Flex.between]}
                            onLayout={onHeaderLayout}
                        >
                            <View onLayout={onLeftLayout} style={[Flex.row, Flex.start, { minWidth: side_width }]}>
                                {render_left?.()}
                            </View>
                            <View style={[Flex.row, Flex.center, Items.center, Gap.xs, { width: center_width }]}>
                                {render_title?.()}
                            </View>
                            <View onLayout={onRightLayout} style={[Flex.row, Flex.end, { minWidth: side_width }]}>
                                {render_right?.()}
                            </View>
                        </View>
                    )}
                    <View style={styles.body} onLayout={handleOnBodyLayout}>
                        {scrollable ? (
                            <KeyboardAwareScrollView style={styles.body} contentContainerStyle={body_style}>
                                {children}
                            </KeyboardAwareScrollView>
                        ) : (
                            <View style={[styles.body, body_style]}>{children}</View>
                        )}
                    </View>
                    {footer && (
                        <View {...footerProps} style={[styles.footer, { width }, footerProps?.style]}>
                            {footer()}
                        </View>
                    )}
                </Animated.View>
                {confirm && (
                    <ConfirmationModal
                        onConfirm={handle_close}
                        title="Unsaved changes"
                        description="Do you wannt to discard your unsaved changes?"
                        confirmText="Discard changes"
                        confirmVariant="destructive-quiet"
                        onRequestClose={() => setConfirm(false)}
                    />
                )}
            </PaperTheme>
        </Modal>
    );
};

const styles = StyleSheet.create({
    body: {
        flex: 1,
    },
    footer: {
        alignSelf: "flex-end",
        paddingBottom: 16,
        alignItems: "center",
    },
});
